// SPDX-FileCopyrightText: 2026 Povl Filip Sonne-Frederiksen
//
// SPDX-License-Identifier: GPL-3.0-or-later

/**
 * The report's cover image choice (`GET|PUT|DELETE /reports/cover`,
 * `PUT /reports/cover/image`; reusex-ufcm.7). `supported` is false on a ruxd
 * from before it (404), and the picker is then hidden. Writes go on the
 * page's own chain (they change no survey state) and are refused for a
 * viewer (`useCanEdit`); `version` counts successful writes, so the cover
 * image URL and the report preview both notice a change.
 */

import { useCallback, useEffect, useState } from 'react';

import { api, ApiRequestError } from '../../api/client';
import type { ReportCover, ResourceView } from '../../api/types';
import { useCanEdit } from '../CaseRoleContext';
import { useMutationQueue } from '../useMutationQueue';

/** The upload limit the contract states. */
export const COVER_MAX_BYTES = 20 * 1024 * 1024;
export const COVER_TYPES = ['image/jpeg', 'image/png'] as const;

export interface ReportCoverState {
  /** Null while the first read is under way. */
  supported: boolean | null;
  cover: ReportCover | null;
  /** Bumped after every successful write. */
  version: number;
  busy: boolean;
  canEdit: boolean;
  choose: (view: ResourceView) => Promise<void>;
  upload: (file: File) => Promise<void>;
  /** "Brug modelbillede": back to the render. */
  clear: () => Promise<void>;
}

/** Why a file cannot be the cover, in Danish; null when it can. */
export function coverFileProblem(file: { type: string; size: number }): string | null {
  if (!(COVER_TYPES as readonly string[]).includes(file.type)) return 'Forsidebilledet skal være en JPEG eller PNG.';
  if (file.size > COVER_MAX_BYTES) return 'Billedet er større end 20 MB.';
  return null;
}

export function useReportCover(notify: (message: string) => void): ReportCoverState {
  const canEdit = useCanEdit();
  const [supported, setSupported] = useState<boolean | null>(null);
  const [cover, setCover] = useState<ReportCover | null>(null);
  const [version, setVersion] = useState(0);
  const queue = useMutationQueue({
    scope: 'page',
    onError: (cause) => notify(`Forsidebilledet kunne ikke gemmes: ${cause instanceof Error ? cause.message : String(cause)}`),
  });

  useEffect(() => {
    const ctrl = new AbortController();
    api
      .reportCover(ctrl.signal)
      .then((c) => {
        setCover(c);
        setSupported(true);
      })
      .catch((cause: unknown) => {
        if (ctrl.signal.aborted) return;
        // 404 = a ruxd from before the cover; anything else: no picker either, quietly.
        setSupported(false);
        if (!(cause instanceof ApiRequestError && cause.status === 404)) console.warn('GET /reports/cover failed', cause);
      });
    return () => ctrl.abort();
  }, []);

  const write = useCallback(
    (send: () => Promise<ReportCover>) =>
      queue.mutate(async () => {
        const next = await send();
        setCover(next);
        setVersion((v) => v + 1);
      }),
    [queue.mutate],
  );

  return {
    supported,
    cover,
    version,
    busy: queue.busy,
    canEdit,
    choose: (view) => write(() => api.setReportCover(view)),
    upload: (file) => {
      const problem = coverFileProblem(file);
      if (problem) {
        notify(problem);
        return Promise.resolve();
      }
      return write(() => api.uploadReportCover(file));
    },
    clear: () => write(() => api.clearReportCover()),
  };
}
